import { Divider, TextLink } from '@ui/themed';
import { t } from 'i18next';
import type { ReactNode } from 'react';
import { PageSection } from '../../../../components/page-section';
import { SettingToggleRow } from '../../../../components/setting-toggle-row';
import { useSettings } from '../../../../context/settings-context';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { DOCS_URLS } from '../../constants';
import { useStorefrontOptionsControls } from '../../context/storefront-options-context';
import type { AccountSectionConfiguration, AccountSectionId } from '../../types/spark-options';
import { AccountSectionsEditor } from '../account-sections-editor';
import { OptionsCard } from '../options-card';

const PAGE = 'features:storefront_options.pages.storefront_options_page' satisfies I18nPrefix;
const CARD = `${PAGE}.cards.my_account_settings` satisfies I18nPrefix;

export function MyAccountSettingsCard({ onRevealSlots }: { onRevealSlots: () => void }) {
  const { options, setOptions, setDisplay, siteSettings } = useStorefrontOptionsControls();
  const { display } = options;
  // Payment status in My Account is only served by the Customers API.
  const { useCustomersAPI } = useSettings();

  const myAccount = display.sectionConfiguration['my-account'];
  const hideFinancials = display.hideFinancials ?? false;
  const showMyAccountSlot = display.showSlots.includes('spark-slot-my-account');
  // Filled together so a forced-hidden section and its explanation can't drift apart.
  const forcedHiddenSections: AccountSectionId[] = [];
  const disabledSectionReasons: Partial<Record<AccountSectionId, ReactNode>> = {};

  if (hideFinancials) {
    forcedHiddenSections.push('financials');
    disabledSectionReasons.financials = t(`${CARD}.financials_hidden_by_hide_financials`);
  }

  if (!showMyAccountSlot) {
    forcedHiddenSections.push('spark-slot-my-account');
    disabledSectionReasons['spark-slot-my-account'] = (
      <>
        {t(`${CARD}.my_account_slot_hidden_by_show_slots`)}{' '}
        <TextLink size="sm" variant="secondary" onClick={onRevealSlots}>
          {t(`${PAGE}.cards.slots.title`)}
        </TextLink>
      </>
    );
  }

  function setMyAccount(config: AccountSectionConfiguration) {
    setOptions((prev) => ({
      ...prev,
      display: {
        ...prev.display,
        sectionConfiguration: {
          ...prev.display.sectionConfiguration,
          'my-account': config,
        },
      },
    }));
  }

  return (
    <OptionsCard title={t(`${CARD}.title`)} subtitle={t(`${CARD}.subtitle`)}>
      <SettingToggleRow
        label={t(`${CARD}.fields.hide_financials.label`)}
        description={t(`${CARD}.fields.hide_financials.description`)}
        checked={hideFinancials}
        onChange={(checked) => setDisplay('hideFinancials', checked)}
        learnMoreUrl={DOCS_URLS.myAccount.hideFinancials}
      />
      <SettingToggleRow
        label={t(`${CARD}.fields.order_list_shipping_address.label`)}
        description={t(`${CARD}.fields.order_list_shipping_address.description`)}
        checked={display.orderListShowShippingAddress}
        onChange={(checked) => setDisplay('orderListShowShippingAddress', checked)}
        learnMoreUrl={DOCS_URLS.myAccount.orderListShippingAddress}
      />
      {useCustomersAPI && (
        <SettingToggleRow
          label={t(`${CARD}.fields.payment_visibility.label`)}
          description={t(`${CARD}.fields.payment_visibility.description`)}
          checked={siteSettings.values.paymentVisibilityEnabled}
          onChange={(checked) => siteSettings.set('paymentVisibilityEnabled', checked)}
          learnMoreUrl={DOCS_URLS.myAccount.paymentVisibility}
        />
      )}
      <SettingToggleRow
        label={t(`${CARD}.fields.purchase_timeline.label`)}
        description={t(`${CARD}.fields.purchase_timeline.description`)}
        checked={siteSettings.values.purchaseTimelineEnabled}
        onChange={(checked) => siteSettings.set('purchaseTimelineEnabled', checked)}
        learnMoreUrl={DOCS_URLS.myAccount.purchaseTimeline}
      />

      <Divider />

      <PageSection
        headingLevel="h3"
        title={t(`${CARD}.sections.account_sections.title`)}
        description={t(`${CARD}.sections.account_sections.description`)}
        learnMoreUrl={DOCS_URLS.myAccount.accountSections}
      >
        <AccountSectionsEditor
          values={myAccount}
          onChange={setMyAccount}
          forcedHiddenSections={forcedHiddenSections}
          disabledSectionReasons={disabledSectionReasons}
        />
      </PageSection>
    </OptionsCard>
  );
}
